import {
  JSX,
  splitProps,
  createContext,
  Accessor,
  createSignal,
  useContext,
  createMemo,
  Show,
  createEffect,
  onCleanup,
} from 'solid-js';
import { mergeRefs, resolveElements } from '@solid-primitives/refs';
import { createEventListener } from '@solid-primitives/event-listener';

import { useIsActive } from './Active';
// import {} from '@solid-primitives/pointer';
import { rootScrollElement } from '../utils';
import { cx } from '../utils';

/**
 * 这是一个滚动视图的实现，
 * 有以下特性
 * 1、修复ios滚动嵌套时滚动卡顿
 * 2、手机上滚动嵌套时禁用其它滚动，保证同时只有一个滚动实例
 * 3、可选的优先级是否禁用系统滚动
 * 4、适配cache时的滚动条状态恢复
 * @param props
 * @returns
 */
export default function ScrollView(props: Props) {
  const [, others] = splitProps(props, ['pan', 'children']);
  const [scrollEl, setScrollEl] = createSignal<HTMLDivElement>(undefined as any);

  /** 临时检测垂直滚动嵌套, 不允许滚动嵌套 */
  // const location = useLocation();
  // const scroll = useScrollElement();
  // createEffect(() => {
  //   const el = scroll();
  //   const sel = scrollEl();
  //   if (el !== (window as any) && !sel.classList.contains('scroll-x')) {
  //     alert(`Scroll Nesting!!!\npath: ${location.pathname}`);
  //   }
  // });
  const isY = createMemo(() => props.pan !== 'x');

  return (
    <ScrollProvider.Provider value={scrollEl}>
      <KeepScroll>
        <div
          {...others}
          ref={mergeRefs(props.ref, setScrollEl)}
          class={cx(isY() ? 'scroll-y' : 'scroll-x', props.class)}
        >
          <Show when={isY()} fallback={props.children}>
            <div class="scroll-container">{props.children}</div>
          </Show>
        </div>
      </KeepScroll>
    </ScrollProvider.Provider>
  );
}

const ScrollProvider = createContext<Accessor<Element | Window>>(() => window);

interface Props extends JSX.HTMLAttributes<HTMLDivElement> {
  pan?: 'x' | 'y';
}

export function useScrollElement() {
  return useContext(ScrollProvider);
}

/** 获取当前滚动高度 */
export function useScrollTop(enable: Accessor<boolean> = () => true) {
  const [scroll, setScroll] = createSignal(0);
  const scrollElement = useScrollElement();

  createEventListener(
    scrollElement,
    () => (enable() ? 'scroll' : ''),
    (e) => {
      const target = e.target === document ? rootScrollElement : (e.target as Element);
      setScroll(target.scrollTop);
    },
    {
      passive: true,
    }
  );

  return scroll;
}

/**
 * 恢复滚动条信息, 用于缓存dom的时候
 * 一般有两种情况会工作，当上下文active状态为false的时候，或者children为空的时候
 * @param props
 * @returns
 */
export function KeepScroll(props: ParentProps) {
  const scroll = {
    scrollTop: 0,
    scrollLeft: 0,
  };
  const active = useIsActive();
  const children = resolveElements(() => props.children);
  const firstChild = createMemo(() => {
    const els = children();
    return active() ? (els instanceof Array ? els[0] : els) : null;
  });
  createComputed(() => {
    const el = firstChild();
    if (el) {
      onCleanup(() => {
        scroll.scrollTop = el.scrollTop;
        scroll.scrollLeft = el.scrollLeft;
      });
    }
  });
  createEffect(() => {
    const el = firstChild();
    if (el) {
      el.scrollTop = scroll.scrollTop;
      el.scrollLeft = scroll.scrollLeft;
    }
  });
  return children as unknown as JSX.Element;
}
